import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { User, LogOut, Settings, LayoutDashboard, Mic, Shield, CreditCard, Crown, UserCircle, ListMusic, Ticket, Menu, X, Bell, Sparkles, KeyRound, Play, Check, UserPlus, FileText, Lock, Upload, Edit2, ChevronDown } from 'lucide-react';
import { RedeemCodeModal } from './RedeemCodeModal';
import { ReferralModal } from './ReferralModal';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 90_000; // 90 s — no need to hammer the server every 60 s

// ── Single notification fetcher — called ONCE per mounted Header ─────────────
// `isAuthenticated` gates the effect; `accessToken` is read via ref so that a
// normal token-refresh cycle does NOT produce a new `fetch` reference, which
// would cancel and restart the interval and fire a duplicate GET /notifications.
function useNotifications(isAuthenticated, accessToken) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const tokenRef = useRef(accessToken);
  useEffect(() => { tokenRef.current = accessToken; }, [accessToken]);

  const fetch = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  }, []); // stable — token is read via ref, not captured in closure

  useEffect(() => {
    if (!isAuthenticated) return;
    fetch();
    const id = setInterval(fetch, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [isAuthenticated, fetch]);

  return { notifications, setNotifications, unreadCount, setUnreadCount };
}

// ── Pure-display notification bell — receives state as props ─────────────────
function NotificationBell({ accessToken, notifications, unreadCount, setNotifications, setUnreadCount }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Tracks creator-invitation notifications acted on in this session:
  // notif.id -> 'accepted' | 'declined' (hides the ✓/✗ buttons after action).
  const [handledInvites, setHandledInvites] = useState({});

  const markReadSilently = async (notif) => {
    if (notif.read) return;
    try {
      await axios.post(`${API}/notifications/${notif.id}/read`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const acceptInvite = async (notif) => {
    // Becoming a creator needs a name/bio, so finish on the creator page
    // (which also re-issues the session after the role change).
    await markReadSilently(notif);
    setHandledInvites(prev => ({ ...prev, [notif.id]: 'accepted' }));
    setOpen(false);
    navigate('/creator');
  };

  const declineInvite = async (notif) => {
    try {
      await axios.post(`${API}/creators/invitation/decline`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      await markReadSilently(notif);
      setHandledInvites(prev => ({ ...prev, [notif.id]: 'declined' }));
      toast.success('Pozvánka odmítnuta');
    } catch {
      toast.error('Nepodařilo se odmítnout pozvánku');
    }
  };

  const markRead = async (notif) => {
    if (!notif.read) {
      try {
        await axios.post(`${API}/notifications/${notif.id}/read`, {}, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {}
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  const markAllRead = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const typeIcon = (type) => {
    switch (type) {
      case 'welcome':             return <Sparkles className="w-4 h-4 text-blue-400" />;
      case 'video_unlocked':      return <KeyRound className="w-4 h-4 text-primary" />;
      case 'subscription_active': return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'new_episode':         return <Play className="w-4 h-4 text-green-500" />;
      case 'creator_invitation':  return <UserPlus className="w-4 h-4 text-violet-400" />;
      default:                    return <Bell className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const typeBg = (type) => {
    switch (type) {
      case 'welcome':             return 'bg-blue-500/10';
      case 'video_unlocked':      return 'bg-primary/10';
      case 'subscription_active': return 'bg-yellow-500/10';
      case 'new_episode':         return 'bg-green-500/10';
      case 'creator_invitation':  return 'bg-violet-500/10';
      default:                    return 'bg-secondary';
    }
  };

  const relativeTime = (iso) => {
    const diff = (Date.now() - new Date(iso)) / 1000;
    if (diff < 60)    return 'Právě teď';
    if (diff < 3600)  return `Před ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Před ${Math.floor(diff / 3600)} h`;
    return `Před ${Math.floor(diff / 86400)} d`;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-trigger">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notifications-dropdown">
        {/* Header row */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-semibold text-foreground text-sm">Notifikace</span>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-primary hover:underline"
            >
              Označit vše jako přečtené
            </button>
          )}
        </div>

        {/* List */}
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-25" />
              Žádné notifikace
            </div>
          ) : (
            notifications.map(notif => {
              // Actionable creator invitation: render inline ✓ / ✗ instead of a
              // plain navigate row (a nested <button> would be invalid HTML).
              if (notif.type === 'creator_invitation') {
                const handled = handledInvites[notif.id];
                return (
                  <div
                    key={notif.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-border/40 last:border-b-0 ${!notif.read ? 'bg-primary/5' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${typeBg(notif.type)}`}>
                      {typeIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium leading-snug ${!notif.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
                        {notif.body}
                      </p>
                      {handled ? (
                        <p className={`text-[11px] mt-1.5 font-medium ${handled === 'accepted' ? 'text-green-500' : 'text-muted-foreground'}`}>
                          {handled === 'accepted' ? '✓ Přijato' : '✗ Odmítnuto'}
                        </p>
                      ) : (
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            className="h-7 px-2.5 gap-1 bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => acceptInvite(notif)}
                            data-testid="accept-invite-notif"
                          >
                            <Check className="w-3.5 h-3.5" /> Přijmout
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2.5 gap-1"
                            onClick={() => declineInvite(notif)}
                            data-testid="decline-invite-notif"
                          >
                            <X className="w-3.5 h-3.5" /> Odmítnout
                          </Button>
                        </div>
                      )}
                      <p className="text-[11px] text-muted-foreground/60 mt-1">
                        {relativeTime(notif.created_at)}
                      </p>
                    </div>
                  </div>
                );
              }
              return (
                <button
                  key={notif.id}
                  onClick={() => markRead(notif)}
                  className={`
                    w-full flex items-start gap-3 px-4 py-3 text-left
                    hover:bg-secondary/60 transition-colors
                    border-b border-border/40 last:border-b-0
                    ${!notif.read ? 'bg-primary/5' : ''}
                  `}
                >
                  {/* Icon bubble */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${typeBg(notif.type)}`}>
                    {typeIcon(notif.type)}
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium leading-snug ${!notif.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {notif.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
                      {notif.body}
                    </p>
                    <p className="text-[11px] text-muted-foreground/60 mt-1">
                      {relativeTime(notif.created_at)}
                    </p>
                  </div>

                  {/* Unread dot */}
                  {!notif.read && (
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const Header = () => {
  const { user, isAuthenticated, logout, isCreator, isAdmin, isPro, accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // A user's referral link is simply /pro-tvurce?ref=<their user id>. window.origin
  // keeps it correct across environments (localhost, onrender, custom domain).
  const referralLink = user?.id
    ? `${window.location.origin}/pro-tvurce?ref=${user.id}`
    : `${window.location.origin}/pro-tvurce`;

  // Single notification fetch — shared between desktop bell and mobile bell
  const notifProps = useNotifications(isAuthenticated, accessToken);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isLandingPage = location.pathname === '/';

  const navTo = (path) => { navigate(path); setMobileMenuOpen(false); };

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to={isAuthenticated ? "/dashboard" : "/"}
            className="text-xl font-bold tracking-tight text-foreground hover:text-primary transition-colors duration-200"
            data-testid="logo-link"
          >
            OneSub
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-muted-foreground hover:text-foreground" data-testid="nav-dashboard">
                    Knihovna
                  </Button>
                </Link>
                {isCreator && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary/10 border border-primary/30 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                        data-testid="nav-creator-menu"
                      >
                        <Mic className="w-4 h-4" />Studio tvůrce
                        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem onClick={() => navigate('/creator')} data-testid="nav-creator"><Mic className="w-4 h-4 mr-2" />Moje epizody</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/creator?action=upload')} data-testid="nav-creator-upload"><Upload className="w-4 h-4 mr-2" />Nahrát epizodu</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/creator?action=edit')} data-testid="nav-creator-edit"><Edit2 className="w-4 h-4 mr-2" />Upravit kanál</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-orange-500/10 border border-orange-500/30 text-orange-500 text-sm font-medium hover:bg-orange-500/20 transition-colors"
                    data-testid="nav-admin"
                  >
                    <Shield className="w-4 h-4" />Administrace
                  </Link>
                )}
                {!isCreator && !isAdmin && (isPro ? (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-500/10 border border-green-500/30 text-green-500 text-sm font-medium">
                    <Crown className="w-4 h-4" />Aktivní členství
                  </div>
                ) : (
                  <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary/10" onClick={() => setRedeemOpen(true)} data-testid="nav-redeem-code">
                    <Ticket className="w-4 h-4 mr-2" />Uplatnit kód
                  </Button>
                ))}
                {!isPro && !isCreator && !isAdmin && (
                  <Link to="/subscribe">
                    <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10" data-testid="nav-upgrade">
                      <Crown className="w-4 h-4 mr-2" />Upgrade
                    </Button>
                  </Link>
                )}
                {!isCreator && !isAdmin && (
                  <Button
                    variant="ghost"
                    className="hidden lg:inline-flex text-muted-foreground hover:text-foreground"
                    onClick={() => setReferralOpen(true)}
                    data-testid="nav-referral"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />Chybí vám zde nějaký tvůrce?
                  </Button>
                )}
                <NotificationBell accessToken={accessToken} {...notifProps} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                      <Avatar name={user?.name} src={user?.avatar_url || user?.picture} size={32} />
                      <span className="hidden lg:inline text-sm">{user?.name}</span>
                      {isPro && <Badge className="bg-primary/10 text-primary border-primary/20 text-xs ml-1">Pro</Badge>}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      {isPro && <Badge className="bg-green-500/10 text-green-500 border-green-500/30 mt-2 text-xs"><Crown className="w-3 h-3 mr-1" />Pro členství</Badge>}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}><LayoutDashboard className="w-4 h-4 mr-2" />Knihovna</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/profile')}><UserCircle className="w-4 h-4 mr-2" />Můj profil</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/playlists')}><ListMusic className="w-4 h-4 mr-2" />Moje playlisty</DropdownMenuItem>
                    {!isCreator && !isAdmin && <DropdownMenuItem onClick={() => setReferralOpen(true)}><UserPlus className="w-4 h-4 mr-2" />Chybí vám zde tvůrce?</DropdownMenuItem>}
                    {isCreator && <DropdownMenuItem onClick={() => navigate('/creator')}><Mic className="w-4 h-4 mr-2" />Moje epizody</DropdownMenuItem>}
                    {isAdmin && <DropdownMenuItem onClick={() => navigate('/admin')}><Shield className="w-4 h-4 mr-2" />Administrace</DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    {!isCreator && !isAdmin && (isPro
                      ? <DropdownMenuItem onClick={() => navigate('/subscribe')}><CreditCard className="w-4 h-4 mr-2" />Správa předplatného</DropdownMenuItem>
                      : <DropdownMenuItem onClick={() => navigate('/subscribe')}><Crown className="w-4 h-4 mr-2" />Získat Pro</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    {!isPro && !isCreator && !isAdmin && (
                      <DropdownMenuItem onClick={() => setRedeemOpen(true)}><Ticket className="w-4 h-4 mr-2" />Uplatnit kód</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/terms')}><FileText className="w-4 h-4 mr-2" />Obchodní podmínky</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/privacy')}><Lock className="w-4 h-4 mr-2" />Ochrana osobních údajů</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Odhlásit se</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {!isLandingPage && <Link to="/login"><Button variant="ghost" data-testid="nav-login">Přihlásit se</Button></Link>}
                <Link to="/register"><Button className="bg-primary text-white hover:bg-primary/90" data-testid="nav-register">Získat přístup</Button></Link>
              </>
            )}
          </nav>

          {/* Mobile right side */}
          <div className="flex md:hidden items-center gap-2">
            {isAuthenticated && (
              <>
                <NotificationBell accessToken={accessToken} {...notifProps} />
                <Avatar name={user?.name} src={user?.avatar_url || user?.picture} size={32} />
              </>
            )}
            <button
              onClick={() => setMobileMenuOpen(o => !o)}
              className="p-2 rounded-lg text-foreground hover:bg-secondary transition-colors"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-background/98 backdrop-blur-lg">
          <div className="container-app py-3 space-y-1">
            {isAuthenticated ? (
              <>
                <div className="px-3 py-2 mb-2">
                  <p className="font-medium text-foreground">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
                <button onClick={() => navTo('/dashboard')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                  <LayoutDashboard className="w-5 h-5" />Knihovna
                </button>
                <button onClick={() => navTo('/profile')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                  <UserCircle className="w-5 h-5" />Můj profil
                </button>
                <button onClick={() => navTo('/playlists')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                  <ListMusic className="w-5 h-5" />Moje playlisty
                </button>
                {isCreator && (
                  <>
                    <button onClick={() => navTo('/creator')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                      <Mic className="w-5 h-5" />Moje epizody
                    </button>
                    <button onClick={() => navTo('/creator?action=upload')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                      <Upload className="w-5 h-5" />Nahrát epizodu
                    </button>
                    <button onClick={() => navTo('/creator?action=edit')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                      <Edit2 className="w-5 h-5" />Upravit kanál
                    </button>
                  </>
                )}
                {isAdmin && (
                  <button onClick={() => navTo('/admin')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                    <Shield className="w-5 h-5" />Administrace
                  </button>
                )}
                {!isCreator && !isAdmin && (
                  <button onClick={() => navTo('/subscribe')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                    <Crown className="w-5 h-5 text-primary" />{isPro ? 'Správa předplatného' : 'Získat Pro'}
                  </button>
                )}
                {!isPro && !isCreator && !isAdmin && (
                  <button onClick={() => { setRedeemOpen(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                    <Ticket className="w-5 h-5" />Uplatnit kód
                  </button>
                )}
                {!isCreator && !isAdmin && (
                  <button onClick={() => { setReferralOpen(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                    <UserPlus className="w-5 h-5" />Chybí vám zde nějaký tvůrce?
                  </button>
                )}
                <div className="border-t border-border mt-2 pt-2">
                  <button onClick={() => navTo('/terms')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                    <FileText className="w-5 h-5" />Obchodní podmínky
                  </button>
                  <button onClick={() => navTo('/privacy')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                    <Lock className="w-5 h-5" />Ochrana osobních údajů
                  </button>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-red-500 text-left">
                    <LogOut className="w-5 h-5" />Odhlásit se
                  </button>
                </div>
              </>
            ) : (
              <>
                <button onClick={() => navTo('/login')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-foreground text-left">
                  Přihlásit se
                </button>
                <button onClick={() => navTo('/register')} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg bg-primary text-white text-left">
                  Získat přístup
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <RedeemCodeModal open={redeemOpen} onOpenChange={setRedeemOpen} />
      <ReferralModal open={referralOpen} onOpenChange={setReferralOpen} referralLink={referralLink} />
    </header>
  );
};

export default Header;
